/**
 * 네이티브 앱(React Native WebView) 연동 훅
 * - WebView 환경 감지
 * - 네이티브 앱과의 통신 인터페이스
 *
 * 파일 브릿지 규약(앱 구현 시 참고 — 웹 쪽은 준비돼 있고 앱이 지원을 선언하면 자동 사용):
 *  - 앱 → 웹: WebView injectedJavaScript 에서 `window.nativeCapabilities = { fileBridge: true }` 설정.
 *  - 웹 → 앱: postMessage JSON `{ type: "downloadFile" | "openFile", payload: NativeFilePayload }`.
 *  - 앱 동작: payload.downloadUrl(API 상대경로, 예 /api/v1/chat/attachments/123/download)에
 *    앱 저장소의 Bearer 토큰을 Authorization 헤더로 붙여 받아 앱 문서 폴더(예 chat/{attachmentId}_{fileName})에
 *    저장한 뒤 OS 기본 뷰어로 연다. openFile 은 파일이 있으면 바로 열고, 없으면(사용자가 지움) 다시 받아서 연다.
 *    downloadFile 은 항상 새로 받아 덮어쓴 뒤 연다.
 */

import { useCallback } from 'react';

/** 파일 브릿지 페이로드. downloadUrl 에는 토큰을 싣지 않는다(앱이 자기 저장소의 토큰을 사용). */
export interface NativeFilePayload {
  attachmentId: number;
  fileName: string;
  mimeType?: string | null;
  fileSize?: number;
  /** API 상대경로 (예: /api/v1/chat/attachments/123/download) */
  downloadUrl: string;
}

export interface NativeAppInterface {
  /** React Native WebView 환경 여부 */
  isWebView: boolean;
  /** iOS 기기 여부 */
  isIOS: boolean;
  /** Android 기기 여부 */
  isAndroid: boolean;
  /** 네이티브 앱에 메시지 전송 */
  postMessage: (type: string, payload?: unknown) => void;
  /** 뒤로가기 (네이티브 or 브라우저) */
  goBack: () => void;
  /** 햅틱 피드백 */
  vibrate: (pattern?: number[]) => void;
  /** 토스트 메시지 표시 */
  showToast: (message: string) => void;
  /** 네이티브 저장소에 토큰 저장 */
  saveToken: (token: string, refreshToken?: string) => void;
  /** 네이티브 저장소에서 토큰 조회 (Promise) */
  getToken: () => Promise<{ token: string | null; refreshToken: string | null }>;
  /** 네이티브 저장소에서 토큰 삭제 */
  clearToken: () => void;
  /** 앱이 파일 저장/열기 브릿지를 지원하는지(앱이 window.nativeCapabilities.fileBridge = true 주입) */
  supportsFileBridge: boolean;
  /** 앱 저장소에 파일을 새로 받아(덮어쓰기) OS 뷰어로 열기 */
  downloadFile: (payload: NativeFilePayload) => void;
  /** 앱 저장소의 파일을 OS 뷰어로 열기. 파일이 없으면 앱이 다시 받아서 연다 */
  openFile: (payload: NativeFilePayload) => void;
}

// 네이티브 앱에서 토큰 응답을 받기 위한 콜백 저장소
const tokenCallbacks = new Map<string, (data: { token: string | null; refreshToken: string | null }) => void>();

declare global {
  interface Window {
    isReactNativeWebView?: boolean;
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
    // 네이티브 앱에서 호출하는 토큰 응답 핸들러
    onNativeTokenResponse?: (requestId: string, token: string | null, refreshToken: string | null) => void;
    // 네이티브 앱이 injectedJavaScript 로 선언하는 지원 기능 목록
    nativeCapabilities?: { fileBridge?: boolean };
  }
}

// 네이티브 앱에서 토큰 응답을 받기 위한 전역 핸들러 설정
if (typeof window !== 'undefined') {
  window.onNativeTokenResponse = (requestId: string, token: string | null, refreshToken: string | null) => {
    const callback = tokenCallbacks.get(requestId);
    if (callback) {
      callback({ token, refreshToken });
      tokenCallbacks.delete(requestId);
    }
  };
}

export function useNativeApp(): NativeAppInterface {
  const isWebView = typeof window !== 'undefined' && !!window.isReactNativeWebView;
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = /iPhone|iPad|iPod/.test(userAgent);
  const isAndroid = /Android/.test(userAgent);

  const postMessage = useCallback((type: string, payload?: unknown) => {
    if (isWebView && window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type, payload }));
    }
  }, [isWebView]);

  const goBack = useCallback(() => {
    if (isWebView) {
      postMessage('navigation', { action: 'back' });
    } else {
      window.history.back();
    }
  }, [isWebView, postMessage]);

  const vibrate = useCallback((pattern: number[] = [50]) => {
    if (isWebView) {
      postMessage('haptic', { pattern });
    } else if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  }, [isWebView, postMessage]);

  const showToast = useCallback((message: string) => {
    if (isWebView) {
      postMessage('toast', { message });
    }
  }, [isWebView, postMessage]);

  // 네이티브 저장소에 토큰 저장
  const saveToken = useCallback((token: string, refreshToken?: string) => {
    if (isWebView) {
      postMessage('saveToken', { token, refreshToken });
    }
  }, [isWebView, postMessage]);

  // 네이티브 저장소에서 토큰 조회
  const getToken = useCallback((): Promise<{ token: string | null; refreshToken: string | null }> => {
    return new Promise((resolve) => {
      if (!isWebView) {
        // WebView가 아니면 localStorage에서 조회
        resolve({
          token: localStorage.getItem('access_token'),
          refreshToken: localStorage.getItem('refresh_token'),
        });
        return;
      }

      // 요청 ID 생성
      const requestId = `token_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // 콜백 등록
      tokenCallbacks.set(requestId, resolve);

      // 네이티브에 토큰 요청
      postMessage('getToken', { requestId });

      // 3초 타임아웃 (네이티브 응답이 없으면 null 반환)
      setTimeout(() => {
        if (tokenCallbacks.has(requestId)) {
          tokenCallbacks.delete(requestId);
          resolve({ token: null, refreshToken: null });
        }
      }, 3000);
    });
  }, [isWebView, postMessage]);

  // 네이티브 저장소에서 토큰 삭제
  const clearToken = useCallback(() => {
    if (isWebView) {
      postMessage('clearToken', {});
    }
  }, [isWebView, postMessage]);

  const supportsFileBridge = isWebView && !!window.nativeCapabilities?.fileBridge;

  const downloadFile = useCallback((payload: NativeFilePayload) => {
    if (isWebView) {
      postMessage('downloadFile', payload);
    }
  }, [isWebView, postMessage]);

  const openFile = useCallback((payload: NativeFilePayload) => {
    if (isWebView) {
      postMessage('openFile', payload);
    }
  }, [isWebView, postMessage]);

  return {
    isWebView,
    isIOS,
    isAndroid,
    postMessage,
    goBack,
    vibrate,
    showToast,
    saveToken,
    getToken,
    clearToken,
    supportsFileBridge,
    downloadFile,
    openFile,
  };
}
